import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { format, parseISO, startOfDay, addWeeks, isSameDay } from 'date-fns';

const SlotManagementGrid = ({ 
  slots, 
  bookings,
  selectedSlots, 
  onSelectSlot, 
  onSelectAll,
  onDeleteSlot,
  onToggleAvailability,
  onAcceptBooking,
  onDeclineBooking,
  onRescheduleBooking,
  filterWeek
}) => {
  const getSlotStatus = (slot) => {
    const booking = bookings?.find(b => b?.slotId === slot?.id && b?.status !== 'cancelled');
    
    if (booking) {
      if (booking?.status === 'confirmed') return 'confirmed';
      if (booking?.status === 'pending') return 'pending';
    }
    
    if (!slot?.isAvailable) return 'blocked';
    return 'available';
  };

  const getStatusColor = (status) => {
    const colors = {
      'available': 'bg-[#D1F5F0] text-[#1FB5A5] border-[#1FB5A5]/30',
      'confirmed': 'bg-green-100 text-green-800 border-green-200',
      'blocked': 'bg-red-100 text-red-800 border-red-200',
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'available': 'CheckCircle',
      'confirmed': 'CheckCircle',
      'blocked': 'XCircle',
      'pending': 'Clock'
    };
    return icons?.[status] || 'Circle';
  };

  const filterSlotsByWeek = (slots) => {
    if (filterWeek === 'all') return slots;
    
    const now = new Date();
    const today = startOfDay(now);
    const currentDayOfWeek = today?.getDay();
    
    let firstMonday;
    if (currentDayOfWeek === 1) {
      firstMonday = today;
    } else if (currentDayOfWeek === 0) {
      firstMonday = addWeeks(today, 0);
    } else {
      const daysUntilMonday = 8 - currentDayOfWeek;
      firstMonday = startOfDay(new Date(today));
      firstMonday?.setDate(firstMonday?.getDate() + daysUntilMonday);
    }

    const weekNumber = parseInt(filterWeek?.replace('week', ''));
    const targetMonday = addWeeks(firstMonday, weekNumber - 1);
    
    return slots?.filter(slot => {
      const slotDate = startOfDay(new Date(slot?.startTime));
      return isSameDay(slotDate, targetMonday);
    });
  };

  const groupSlotsByDate = (slots) => {
    const grouped = {};
    const filteredSlots = filterSlotsByWeek(slots);
    
    filteredSlots?.forEach(slot => {
      try {
        const date = format(parseISO(slot?.startTime), 'yyyy-MM-dd');
        if (!grouped?.[date]) {
          grouped[date] = [];
        }
        grouped?.[date]?.push(slot);
      } catch (error) {
        console.error('Error grouping slot:', error);
      }
    });
    
    return grouped;
  };

  const groupedSlots = groupSlotsByDate(slots);
  const sortedDates = Object.keys(groupedSlots)?.sort();

  if (sortedDates?.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-elevation-1 p-12 text-center">
        <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Monday slots found</h3>
        <p className="text-muted-foreground mb-6">
          {filterWeek === 'all' ?'No Monday consultation slots are currently scheduled.' :'No slots found for the selected week.'}
        </p>
        <Button onClick={() => window.location?.reload()} iconName="RefreshCw" iconPosition="left">
          Refresh
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {sortedDates?.map(date => (
        <div key={date} className="bg-white rounded-xl shadow-elevation-1 overflow-hidden">
          {/* Date Header */}
          <div className="bg-gradient-to-r from-primary/10 to-secondary/10 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Icon name="Calendar" size={20} className="text-primary" />
                <h3 className="text-lg font-semibold text-foreground">
                  {format(parseISO(date), 'EEEE, MMMM dd, yyyy')}
                </h3>
              </div>
              <span className="text-sm text-muted-foreground">
                {groupedSlots?.[date]?.length} slot(s)
              </span>
            </div>
          </div>

          {/* Slots Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left">
                    <input
                      type="checkbox"
                      checked={groupedSlots?.[date]?.every(slot => selectedSlots?.includes(slot?.id))}
                      onChange={() => {
                        const dateSlotIds = groupedSlots?.[date]?.map(s => s?.id);
                        const allSelected = dateSlotIds?.every(id => selectedSlots?.includes(id));
                        if (allSelected) {
                          dateSlotIds?.forEach(id => onSelectSlot(id));
                        } else {
                          dateSlotIds?.forEach(id => {
                            if (!selectedSlots?.includes(id)) {
                              onSelectSlot(id);
                            }
                          });
                        }
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Time Slot
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Booking Details
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {groupedSlots?.[date]?.map((slot) => {
                  const status = getSlotStatus(slot);
                  const booking = bookings?.find(b => b?.slotId === slot?.id && b?.status !== 'cancelled');
                  
                  return (
                    <tr 
                      key={slot?.id}
                      className="hover:bg-gray-50 transition-colors duration-150"
                    >
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedSlots?.includes(slot?.id)}
                          onChange={() => onSelectSlot(slot?.id)}
                          className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Icon name="Clock" size={16} className="text-muted-foreground" />
                          <span className="text-sm font-medium text-foreground">
                            {format(parseISO(slot?.startTime), 'h:mm a')} - {format(parseISO(slot?.endTime), 'h:mm a')}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-muted-foreground">
                          60 minutes
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(status)}`}>
                          <Icon name={getStatusIcon(status)} size={14} />
                          {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {booking ? (
                          <div>
                            <p className="text-sm font-medium text-foreground">{booking?.clientName}</p>
                            <p className="text-xs text-muted-foreground">{booking?.clientEmail}</p>
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">No booking</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          {status === 'pending' && booking && (
                            <>
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => onAcceptBooking?.(booking?.id)}
                                iconName="Check"
                                iconPosition="left"
                                className="bg-[#1FB5A5] hover:bg-[#1BA594]"
                              >
                                Accept
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onRescheduleBooking?.(booking?.id, slot?.id)}
                                iconName="Calendar"
                                iconPosition="left"
                              >
                                Reschedule
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onDeclineBooking?.(booking?.id)}
                                iconName="X"
                                iconPosition="left"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                Decline
                              </Button>
                            </>
                          )}
                          {status === 'available' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onToggleAvailability(slot?.id, slot?.isAvailable)}
                              iconName="Ban"
                              iconPosition="left"
                            >
                              Block
                            </Button>
                          )}
                          {status === 'blocked' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onToggleAvailability(slot?.id, slot?.isAvailable)}
                              iconName="CheckCircle"
                              iconPosition="left"
                            >
                              Unblock
                            </Button>
                          )}
                          {!booking && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onDeleteSlot(slot?.id)}
                              iconName="Trash2"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default SlotManagementGrid;